import {inject} from "@angular/core";
import {CanActivateChildFn, CanActivateFn, Router, UrlTree} from "@angular/router";
import {Auth, IdTokenResult} from "@angular/fire/auth";
import {from, Observable, of, throwError} from "rxjs";
import {catchError, map, switchMap, take} from "rxjs/operators";

import {environment} from "../../environments/environment";
import {EhrClaims} from "../_interfaces/ehr-claims.interface";
import {
  AuthenticationService,
  SessionExpiryReason
} from "../_services/authentication.service";

export const authenticationGuard: CanActivateFn = (_route, state) =>
  checkOfficeAuthentication(state.url);

export const authenticationChildGuard: CanActivateChildFn = (_route, state) =>
  checkOfficeAuthentication(state.url);

function checkOfficeAuthentication(targetUrl: string): Observable<boolean | UrlTree> {
  const auth = inject(Auth);
  const router = inject(Router);
  const authService = inject(AuthenticationService);

  const toLogin = (): UrlTree =>
    router.createUrlTree(["login"], {
      queryParams: {
        redirectURL: targetUrl
      }
    });

  const toVerifyCode = (): UrlTree =>
    router.createUrlTree(["verify-code"], {
      queryParams: {
        redirectURL: targetUrl
      }
    });

  const toReasonLogin = (reason: SessionExpiryReason): UrlTree =>
    router.createUrlTree(["login"], {
      queryParams: {
        reason
      }
    });

  const clearAndRoute = (reason: SessionExpiryReason): Observable<UrlTree> =>
    authService.clearInvalidSessionForRoute$(targetUrl).pipe(
      map(() => toReasonLogin(reason))
    );

  return from(auth.authStateReady()).pipe(
    take(1),
    switchMap(() => {
      const user = auth.currentUser;

      if (!user) {
        return of<IdTokenResult | null>(null);
      }

      return from(user.getIdTokenResult(true));
    }),
    switchMap(token => {
      if (!token) {
        return of<boolean | UrlTree>(toLogin());
      }

      const claims = (token.claims ?? {}) as EhrClaims;

      if (!environment.production) {
        console.log("Auth Guard claims", {
          hasOrgId: !!claims.orgId,
          hasOfficeSessionId: !!claims.officeSessionId,
          hasEmployeeSessionId: !!claims.employeeSessionId,
          hasRole: !!claims.role,
          mfaPending: claims.mfaPending === true,
          hasLocalOfficeSessionId: !!authService.getStoredOfficeSessionId(),
          hasPendingOfficeVerification: authService.hasPendingOfficeVerification()
        });
      }

      /*
       * This browser requested an Office verification code. It must complete
       * that local login flow before it may enter a secure Office route, even
       * when global Firebase claims still contain another browser's Office
       * session id.
       */
      if (authService.hasPendingOfficeVerification()) {
        return of<boolean | UrlTree>(toVerifyCode());
      }

      /*
       * Employee verification must not block an existing Office session.
       * Office, missing and unknown pending application context remain
       * fail-closed for Office.
       */
      if (authService.isOfficeMfaPending(claims)) {
        return clearAndRoute("server");
      }

      const hasOfficeClaims =
        !!claims.orgId &&
        !!claims.officeSessionId &&
        !!claims.role &&
        claims.role !== "EMPLOYEE";

      if (!hasOfficeClaims) {
        return of<boolean | UrlTree>(toLogin());
      }

      /*
       * A browser may only restore the Office session it established locally.
       * A Firebase claim issued for another browser must not be adopted.
       */
      if (!authService.hasMatchingOfficeSessionBinding(claims)) {
        const reason: SessionExpiryReason = authService.getStoredOfficeSessionId()
          ? "session-mismatch"
          : "server";

        return clearAndRoute(reason);
      }

      return authService.ensureCurrentSession$("restore", claims).pipe(
        take(1),
        switchMap(session => {
          if (authService.isHardSessionExpired(session)) {
            return clearAndRoute("policy");
          }

          if (authService.isBrowserSessionExpired(session)) {
            return authService.expireBrowserSessionForRoute$(targetUrl).pipe(
              map(() => toReasonLogin("policy"))
            );
          }

          if (!authService.isRollingSessionExpired(session)) {
            return of<boolean | UrlTree>(true);
          }

          return authService.refreshRollingSession$(session).pipe(
            switchMap(refreshedSession => {
              if (authService.isHardSessionExpired(refreshedSession)) {
                return clearAndRoute("policy");
              }

              if (authService.isBrowserSessionExpired(refreshedSession)) {
                return authService.expireBrowserSessionForRoute$(targetUrl).pipe(
                  map(() => toReasonLogin("policy"))
                );
              }

              return of<boolean | UrlTree>(true);
            }),
            catchError(error => {
              if (authService.isBootstrapCancelledError(error)) {
                return throwError(() => error);
              }

              const reason = authService.getSessionExpiryReason(error);

              if (reason) {
                return clearAndRoute(reason);
              }

              if (authService.isRetryableSessionRefreshError(error)) {
                if (!environment.production) {
                  console.warn(
                    "Session refresh failed transiently; preserving current Office session",
                    error
                  );
                }

                return of<boolean | UrlTree>(true);
              }

              return of<boolean | UrlTree>(toLogin());
            })
          );
        })
      );
    }),
    catchError(error => {
      if (authService.isBootstrapCancelledError(error)) {
        return of<boolean | UrlTree>(false);
      }

      if (!environment.production) {
        console.error("Error at Auth Guard", error);
      }

      const reason = authService.getSessionExpiryReason(error);

      if (reason) {
        return clearAndRoute(reason);
      }

      return of<boolean | UrlTree>(toLogin());
    })
  );
}
